// src/pages/admin/AdminProperties.js
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import './admin.css';


import {
  PlusIcon,
  XMarkIcon,
  SparklesIcon,
  PhotoIcon,
  VideoCameraIcon,

  TrashIcon,
  PencilIcon,

  HomeIcon,

  FunnelIcon,
  StarIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import {
  fetchProperties,
  createProperty,
  updateProperty,
  deleteProperty,
  toggleFeatured,
  selectProperties,
  selectPropertiesLoading,
  selectOperationLoading,
  selectPagination,
  selectFilters,
  selectError,
  selectSuccess,
  clearError,
  clearSuccess,
  updateFilters
} from '../../redux/slices/propertySlice';

// UPDATED: Default form data with all new fields
const defaultFormData = {
  title: '',
  description: '',
  price: '',
  propertyType: 'apartment',
  listingType: 'sale',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'India',
  latitude: '',
  longitude: '',
  bedrooms: '',
  bathrooms: '',
  squareFootage: '',
  yearBuilt: '',
  parkingSpaces: '',
  images: [],
  videoUrl: '',
  amenities: [],
  featured: false,
  status: 'available',
  agentId: '',
  // NEW FIELDS
  builder: '',
  propertyCategory: '',
  projectStatus: '',
  balcony: '',
  serventRoom: '',
  doorFacing: '',
  plotArea: '',
  superBuiltUpArea: '',
  builtUpArea: '',
  carpetArea: '',
  floorNo: '',
  totalFloors: '',
  furnishing: '',
  availableFrom: '',
  katha: ''
};

const propertyTypes = [
  { value: 'apartment', label: 'Apartment' },
  { value: 'house', label: 'House' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'villa', label: 'Villa' },
  { value: 'duplex', label: 'Duplex' },
  { value: 'studio', label: 'Studio' },
  { value: 'penthouse', label: 'Penthouse' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'land', label: 'Land' },
  { value: 'other', label: 'Other' }
];

const propertyCategories = [
  { value: 'A grade', label: 'A Grade' },
  { value: 'B grade', label: 'B Grade' },
  { value: 'C grade', label: 'C Grade' },
  { value: 'Premium', label: 'Premium' },
  { value: 'Luxury', label: 'Luxury' },
  { value: 'Budget', label: 'Budget' }
];

const projectStatusOptions = [
  { value: 'Completed', label: 'Completed' },
  { value: 'Under Construction', label: 'Under Construction' },
  { value: 'Ready to move in', label: 'Ready to Move In' },
  { value: 'Upcoming', label: 'Upcoming' },
  { value: 'Planned', label: 'Planned' }
];

const furnishingOptions = [
  { value: 'Fully-Furnished', label: 'Fully Furnished' },
  { value: 'Semi-Furnished', label: 'Semi Furnished' },
  { value: 'Bareshell', label: 'Bareshell' },
  { value: 'Unfurnished', label: 'Unfurnished' }
];

const doorFacingOptions = [
  { value: 'North', label: 'North' },
  { value: 'South', label: 'South' },
  { value: 'East', label: 'East' },
  { value: 'West', label: 'West' },
  { value: 'North-East', label: 'North-East' },
  { value: 'North-West', label: 'North-West' },
  { value: 'South-East', label: 'South-East' },
  { value: 'South-West', label: 'South-West' }
];

const kathaTypes = [
  { value: 'A katha', label: 'A Katha' },
  { value: 'B Katha', label: 'B Katha' },
  { value: 'E katha', label: 'E Katha' },
  { value: 'Katha', label: 'Katha' },
  { value: 'No Katha', label: 'No Katha' }
];

const statusOptions = [
  { value: 'available', label: 'Available', color: 'bg-green-100 text-green-800' },
  { value: 'sold', label: 'Sold', color: 'bg-gray-100 text-gray-800' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'under_contract', label: 'Under Contract', color: 'bg-blue-100 text-blue-800' },
  { value: 'withdrawn', label: 'Withdrawn', color: 'bg-red-100 text-red-800' },
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-600' }
];

function AdminProperties() {
  const dispatch = useDispatch();
  const properties = useSelector(selectProperties);
  const loading = useSelector(selectPropertiesLoading);
  const operationLoading = useSelector(selectOperationLoading);
  const pagination = useSelector(selectPagination);
  const filters = useSelector(selectFilters);
  const error = useSelector(selectError);
  const success = useSelector(selectSuccess);

  const [localProperties, setLocalProperties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setLocalProperties(properties);
  }, [properties]);

  useEffect(() => {
    dispatch(fetchProperties(filters));
  }, [dispatch, filters]);

  const handleToggleFeaturedLocal = useCallback((propertyId) => {
    setLocalProperties(prevProperties => {
      return prevProperties.map(property => {
        if (property.id === propertyId) {
          return {
            ...property,
            featured: !property.featured
          };
        }
        return property;
      });
    });
  }, []);

  const handleToggleFeaturedBackend = useCallback(async (id) => {
    await dispatch(toggleFeatured(id));
  }, [dispatch]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData(defaultFormData);
    setEditingProperty(null);
  }, []);

  const handleEdit = useCallback((property) => {
    setEditingProperty(property);
    setFormData({
      ...defaultFormData,
      ...property,
      images: property.images || [],
      amenities: property.amenities || [],
      price: property.price?.toString() || '',
      bedrooms: property.bedrooms?.toString() || '',
      bathrooms: property.bathrooms?.toString() || '',
      squareFootage: property.squareFootage?.toString() || '',
      yearBuilt: property.yearBuilt?.toString() || '',
      parkingSpaces: property.parkingSpaces?.toString() || '',
      latitude: property.latitude?.toString() || '',
      longitude: property.longitude?.toString() || '',
      balcony: property.balcony?.toString() || '',
      serventRoom: property.serventRoom?.toString() || '',
      plotArea: property.plotArea?.toString() || '',
      superBuiltUpArea: property.superBuiltUpArea?.toString() || '',
      builtUpArea: property.builtUpArea?.toString() || '',
      carpetArea: property.carpetArea?.toString() || '',
      floorNo: property.floorNo?.toString() || '',
      totalFloors: property.totalFloors?.toString() || ''
    });
    setShowForm(true);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      await dispatch(deleteProperty(id));
    }
  }, [dispatch]);

  const handleMediaUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploadingMedia(true);
    try {
      const formDataUpload = new FormData();

      files.forEach(file => {
        formDataUpload.append('media', file);
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/upload/property-media`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: formDataUpload,
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        const uploadedMedia = result.data.map(file => ({
          type: file.type,
          url: file.url,
          public_id: file.public_id,
        }));

        handleInputChange('images', [...formData.images, ...uploadedMedia]);
      } else {
        console.error('Upload failed:', result);
        alert(result.message || 'Upload failed.');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleAddVideoUrl = () => {
    const url = prompt('Enter video URL (YouTube, Vimeo, or direct link):');
    if (url && url.trim()) {
      handleInputChange('videoUrl', url.trim());
    }
  };

  const removeMediaItem = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    handleInputChange('images', newImages);
  };

  const generateAIDescription = async () => {
    if (!formData.title || !formData.address) {
      alert('Please enter title and address to generate AI description');
      return;
    }

    setAiLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/ai/generate-property-description`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: formData.title,
          address: formData.address,
          city: formData.city,
          propertyType: formData.propertyType,
          bedrooms: formData.bedrooms,
          bathrooms: formData.bathrooms,
          squareFootage: formData.squareFootage,
          price: formData.price
        })
      });

      const result = await response.json();
      if (result.success) {
        handleInputChange('description', result.data.description);
      } else {
        throw new Error(result.message || 'Failed to generate description');
      }
    } catch (error) {
      console.error('AI generation failed:', error);
      alert('Failed to generate AI description. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const requiredFields = ['title', 'description', 'price', 'address', 'city', 'state'];
    const missingFields = requiredFields.filter(field => !formData[field]?.toString().trim());
    
    if (missingFields.length > 0) {
      alert(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        bedrooms: parseFloat(formData.bedrooms) || 0,
        bathrooms: parseFloat(formData.bathrooms) || 0,
        squareFootage: formData.squareFootage ? parseInt(formData.squareFootage) : null,
        yearBuilt: formData.yearBuilt ? parseInt(formData.yearBuilt) : null,
        parkingSpaces: parseInt(formData.parkingSpaces) || 0,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        balcony: formData.balcony ? parseInt(formData.balcony) : 0,
        serventRoom: formData.serventRoom ? parseInt(formData.serventRoom) : 0,
        plotArea: formData.plotArea ? parseFloat(formData.plotArea) : null,
        superBuiltUpArea: formData.superBuiltUpArea ? parseFloat(formData.superBuiltUpArea) : null,
        builtUpArea: formData.builtUpArea ? parseFloat(formData.builtUpArea) : null,
        carpetArea: formData.carpetArea ? parseFloat(formData.carpetArea) : null,
        totalFloors: formData.totalFloors ? parseFloat(formData.totalFloors) : null
      };

      if (editingProperty) {
        await dispatch(updateProperty({ id: editingProperty.id, data: submitData }));
      } else {
        await dispatch(createProperty(submitData));
      }
      dispatch(fetchProperties(filters));
      
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Property save failed:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    dispatch(updateFilters({ [field]: value }));
  };

  useEffect(() => {
    if (error) {
      setTimeout(() => dispatch(clearError()), 5000);
    }
    if (success) {
      setTimeout(() => dispatch(clearSuccess()), 3000);
    }
  }, [error, success, dispatch]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price || 0);
  };

  const getDisplayArea = (property) => {
    if (property.carpetArea) return `${property.carpetArea} sq ft`;
    if (property.superBuiltUpArea) return `${property.superBuiltUpArea} sq ft`;
    if (property.squareFootage) return `${property.squareFootage} sq ft`;
    return 'N/A';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <HomeIcon className="w-6 h-6 sm:w-8 sm:h-8 mr-3 text-indigo-600" />
                Property Management
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage your real estate listings</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                <FunnelIcon className="w-5 h-5" />
                Filters
              </button>
             <button
  className="my-violet-btn flex items-center justify-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm sm:text-base"
  onClick={() => { setShowForm(true); resetForm(); }}>
  <PlusIcon className="w-5 h-5" />
  Add Property
</button>


            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
            <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
              <div className="text-blue-600 text-xs sm:text-sm font-medium">Total Properties</div>
              <div className="text-xl sm:text-2xl font-bold text-blue-900">{pagination.totalProperties || 0}</div>
            </div>
            <div className="bg-green-50 p-3 sm:p-4 rounded-lg">
              <div className="text-green-600 text-xs sm:text-sm font-medium">Available</div>
              <div className="text-xl sm:text-2xl font-bold text-green-900">
                {localProperties.filter(p => p.status === 'available').length}
              </div>
            </div>
            <div className="bg-yellow-50 p-3 sm:p-4 rounded-lg">
              <div className="text-yellow-600 text-xs sm:text-sm font-medium">Featured</div>
              <div className="text-xl sm:text-2xl font-bold text-yellow-900">
                {localProperties.filter(p => p.featured).length}
              </div>
            </div>
            <div className="bg-purple-50 p-3 sm:p-4 rounded-lg">
              <div className="text-purple-600 text-xs sm:text-sm font-medium">Total Value</div>
              <div className="text-xl sm:text-2xl font-bold text-purple-900">
                {formatPrice(localProperties.reduce((sum, p) => sum + Number(p.price || 0), 0))}
              </div>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <input
                  type="text"
                  placeholder="Search properties..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
                <select
                  value={filters.propertyType}
                  onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                >
                  <option value="">All Types</option>
                  {propertyTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                >
                  <option value="">All Status</option>
                  {statusOptions.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="Min Price"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
                <input
                  type="number"
                  placeholder="Max Price"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Messages */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 text-sm"
            >
              {error}
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-4 text-sm"
            >
              {success}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Property Form Modal */}
        <AnimatePresence>
          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-lg shadow-xl max-w-6xl w-full my-8"
              >
                <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                      {editingProperty ? 'Edit Property' : 'Add New Property'}
                    </h2>
                    <button
                      type="button"
                      onClick={() => { setShowForm(false); resetForm(); }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Scrollable Form Content */}
                  <div className="max-h-[70vh] overflow-y-auto pr-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Column 1: Basic Information */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Property Title *
                          </label>
                          <input
                            type="text"
                            placeholder="Enter property title"
                            value={formData.title}
                            onChange={(e) => handleInputChange('title', e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description *
                          </label>
                          <div className="flex gap-2">
                            <textarea
                              placeholder="Property description"
                              value={formData.description}
                              onChange={(e) => handleInputChange('description', e.target.value)}
                              required
                              rows={4}
                              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                            <button
                              type="button"
                              onClick={generateAIDescription}
                              disabled={aiLoading}
                              className="self-start flex items-center gap-1 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 text-sm"
                            >
                              <SparklesIcon className="w-4 h-4" />
                              {aiLoading ? 'AI...' : 'AI'}
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Price (₹) *
                          </label>
                          <input
                            type="number"
                            placeholder="0.00"
                            value={formData.price}
                            onChange={(e) => handleInputChange('price', e.target.value)}
                            required
                            min="0"
                            step="0.01"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Property Type *
                          </label>
                          <select
                            value={formData.propertyType}
                            onChange={(e) => handleInputChange('propertyType', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          >
                            {propertyTypes.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Builder/Developer
                          </label>
                          <input
                            type="text"
                            placeholder="Enter builder name"
                            value={formData.builder}
                            onChange={(e) => handleInputChange('builder', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Property Grade
                          </label>
                          <select
                            value={formData.propertyCategory}
                            onChange={(e) => handleInputChange('propertyCategory', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          >
                            <option value="">Select Grade</option>
                            {propertyCategories.map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Project Status
                          </label>
                          <select
                            value={formData.projectStatus}
                            onChange={(e) => handleInputChange('projectStatus', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          >
                            <option value="">Select Status</option>
                            {projectStatusOptions.map(status => (
                              <option key={status.value} value={status.value}>{status.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Listing Type
                          </label>
                          <select
                            value={formData.listingType}
                            onChange={(e) => handleInputChange('listingType', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          >
                            <option value="sale">For Sale</option>
                            <option value="rent">For Rent</option>
                            <option value="lease">For Lease</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Listing Status
                          </label>
                          <select
                            value={formData.status}
                            onChange={(e) => handleInputChange('status', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          >
                            {statusOptions.map(status => (
                              <option key={status.value} value={status.value}>{status.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Column 2: Location & Property Details */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Location & Details</h3>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Street Address *
                          </label>
                          <input
                            type="text"
                            placeholder="Enter street address"
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              City *
                            </label>
                            <input
                              type="text"
                              placeholder="Enter city"
                              value={formData.city}
                              onChange={(e) => handleInputChange('city', e.target.value)}
                              required
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              State *
                            </label>
                            <input
                              type="text"
                              placeholder="Enter state"
                              value={formData.state}
                              onChange={(e) => handleInputChange('state', e.target.value)}
                              required
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Zip Code
                            </label>
                            <input
                              type="text"
                              placeholder="Enter zip code"
                              value={formData.zipCode}
                              onChange={(e) => handleInputChange('zipCode', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Country
                            </label>
                            <input
                              type="text"
                              placeholder="Enter country"
                              value={formData.country}
                              onChange={(e) => handleInputChange('country', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Bedrooms
                            </label>
                            <input
                              type="number"
                              placeholder="0"
                              value={formData.bedrooms}
                              onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                              min="0"
                              step="0.5"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Bathrooms
                            </label>
                            <input
                              type="number"
                              placeholder="0"
                              value={formData.bathrooms}
                              onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                              min="0"
                              step="0.5"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Balconies
                            </label>
                            <input
                              type="number"
                              placeholder="0"
                              value={formData.balcony}
                              onChange={(e) => handleInputChange('balcony', e.target.value)}
                              min="0"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Parking Spaces
                            </label>
                            <input
                              type="number"
                              placeholder="0"
                              value={formData.parkingSpaces}
                              onChange={(e) => handleInputChange('parkingSpaces', e.target.value)}
                              min="0"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Servant Room
                            </label>
                            <select
                              value={formData.serventRoom}
                              onChange={(e) => handleInputChange('serventRoom', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            >
                              <option value="0">No</option>
                              <option value="1">Yes</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Year Built
                          </label>
                          <input
                            type="number"
                            placeholder="YYYY"
                            value={formData.yearBuilt}
                            onChange={(e) => handleInputChange('yearBuilt', e.target.value)}
                            min="1800"
                            max={new Date().getFullYear()}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Furnishing Status
                          </label>
                          <select
                            value={formData.furnishing}
                            onChange={(e) => handleInputChange('furnishing', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          >
                            <option value="">Select Furnishing</option>
                            {furnishingOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Door Facing
                          </label>
                          <select
                            value={formData.doorFacing}
                            onChange={(e) => handleInputChange('doorFacing', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          >
                            <option value="">Select Direction</option>
                            {doorFacingOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Katha Type
                          </label>
                          <select
                            value={formData.katha}
                            onChange={(e) => handleInputChange('katha', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          >
                            <option value="">Select Katha</option>
                            {kathaTypes.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Available From
                          </label>
                          <input
                            type="text"
                            placeholder="e.g., immediately, 2026, Jan 2027"
                            value={formData.availableFrom}
                            onChange={(e) => handleInputChange('availableFrom', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>

                      {/* Column 3: Area Measurements & Media */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Area & Media</h3>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Plot Area (sq ft)
                          </label>
                          <input
                            type="number"
                            placeholder="0"
                            value={formData.plotArea}
                            onChange={(e) => handleInputChange('plotArea', e.target.value)}
                            min="0"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Super Built-up Area (sq ft)
                          </label>
                          <input
                            type="number"
                            placeholder="0"
                            value={formData.superBuiltUpArea}
                            onChange={(e) => handleInputChange('superBuiltUpArea', e.target.value)}
                            min="0"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Built-up Area (sq ft)
                          </label>
                          <input
                            type="number"
                            placeholder="0"
                            value={formData.builtUpArea}
                            onChange={(e) => handleInputChange('builtUpArea', e.target.value)}
                            min="0"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Carpet Area (sq ft)
                          </label>
                          <input
                            type="number"
                            placeholder="0"
                            value={formData.carpetArea}
                            onChange={(e) => handleInputChange('carpetArea', e.target.value)}
                            min="0"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Square Footage (Generic)
                          </label>
                          <input
                            type="number"
                            placeholder="0"
                            value={formData.squareFootage}
                            onChange={(e) => handleInputChange('squareFootage', e.target.value)}
                            min="0"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Floor Number
                          </label>
                          <input
                            type="text"
                            placeholder="e.g., 3, G, 14"
                            value={formData.floorNo}
                            onChange={(e) => handleInputChange('floorNo', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Floors in Building
                          </label>
                          <input
                            type="number"
                            placeholder="0"
                            value={formData.totalFloors}
                            onChange={(e) => handleInputChange('totalFloors', e.target.value)}
                            min="0"
                            step="0.5"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div className="pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Media Upload</h4>
                          
                          <div className="flex gap-2 mb-3">
                            <label className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors text-sm">
                              <PhotoIcon className="w-5 h-5" />
                              <span>Upload Images</span>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleMediaUpload}
                                className="hidden"
                              />
                            </label>
                            
                            <button
                              type="button"
                              onClick={handleAddVideoUrl}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm"
                            >
                              <VideoCameraIcon className="w-5 h-5" />
                              <span>Add Video</span>
                            </button>
                          </div>

                          {uploadingMedia && (
                            <p className="text-sm text-gray-500 animate-pulse mb-2">Uploading...</p>
                          )}

                          {(formData.images.length > 0 || formData.videoUrl) && (
                            <div className="border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                              <div className="grid grid-cols-3 gap-2">
                                {formData.images.map((image, index) => (
                                  <div key={index} className="relative group">
                                    <img
                                      src={image.url}
                                      alt="Property"
                                      className="w-full h-20 object-cover rounded"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeMediaItem(index)}
                                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <XMarkIcon className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                                {formData.videoUrl && (
                                  <div className="relative group">
                                    <div className="w-full h-20 bg-purple-100 rounded flex items-center justify-center">
                                      <VideoCameraIcon className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleInputChange('videoUrl', '')}
                                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <XMarkIcon className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="pt-4 border-t border-gray-200">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.featured}
                              onChange={(e) => handleInputChange('featured', e.target.checked)}
                              className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                            />
                            <span className="text-sm font-medium text-gray-700">Featured Property</span>
                            <StarIcon className="w-4 h-4 text-yellow-500" />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end gap-4 mt-6 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => { setShowForm(false); resetForm(); }}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={operationLoading}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {operationLoading ? 'Saving...' : (editingProperty ? 'Update Property' : 'Create Property')}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Properties Table */}
<div className="bg-white rounded-lg shadow-sm w-full">
  {loading ? (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    </div>
  ) : localProperties.length === 0 ? (
    <div className="text-center py-12">
      <HomeIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-2 text-sm font-medium text-gray-900">No properties</h3>
      <p className="mt-1 text-sm text-gray-500">Get started by creating a new property.</p>
    </div>
  ) : (
    <div className="relative w-full">
      {/* ✅ Scroll container with both scrollbars visible */}
      <div className="w-full overflow-x-scroll overflow-y-scroll max-h-[600px] scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
        <table className="min-w-full divide-y divide-gray-200 table-auto">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Property
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              {/* ✅ Sticky Actions column */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 shadow-lg">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {localProperties.map((property) => {
              const statusOption = statusOptions.find((s) => s.value === property.status);
              return (
                <tr key={property.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        {property.images && property.images.length > 0 ? (
                          <img
                            className="h-12 w-12 rounded-lg object-cover"
                            src={property.images[0].url}
                            alt={property.title}
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                            <HomeIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 flex items-center">
                          {property.title}
                          {property.featured && (
                            <StarIcon className="ml-2 h-4 w-4 text-yellow-500 fill-current" />
                          )}
                        </div>
                        <div className="text-sm text-gray-500 capitalize">
                          {property.propertyType} • {property.listingType}
                        </div>
                        {property.builder && (
                          <div className="text-xs text-gray-400">🏗️ {property.builder}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <MapPinIcon className="mr-1 h-4 w-4 text-gray-400" />
                      <div>
                        <div>
                          {property.city}, {property.state}
                        </div>
                        <div className="text-gray-500">{property.zipCode}</div>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatPrice(property.price)}
                    </div>
                    <div className="text-sm text-gray-500">INR</div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      {property.bedrooms} bed • {property.bathrooms} bath
                    </div>
                    {property.carpetArea && (
                      <div>{Number(property.carpetArea).toLocaleString()} sq ft (Carpet)</div>
                    )}
                    {!property.carpetArea && property.superBuiltUpArea && (
                      <div>{Number(property.superBuiltUpArea).toLocaleString()} sq ft (Super)</div>
                    )}
                    {!property.carpetArea &&
                      !property.superBuiltUpArea &&
                      property.squareFootage && (
                        <div>{Number(property.squareFootage).toLocaleString()} sq ft</div>
                      )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        statusOption?.color || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {statusOption?.label || property.status}
                    </span>
                    {property.projectStatus && (
                      <div className="mt-1">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                          {property.projectStatus}
                        </span>
                      </div>
                    )}
                  </td>

                  {/* ✅ Actions (always visible, not cut off) */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-white shadow-lg">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleEdit(property)}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>

                      <button
                        onClick={() => handleDelete(property.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  )}

  {/* Pagination - ADD THIS AFTER THE TABLE */}
{pagination.totalPages > 1 && (
  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
    {/* Mobile Pagination */}
    <div className="flex-1 flex justify-between sm:hidden">
      <button
        onClick={() => handleFilterChange('page', pagination.currentPage - 1)}
        disabled={!pagination.hasPrev}
        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Previous
      </button>
      <button
        onClick={() => handleFilterChange('page', pagination.currentPage + 1)}
        disabled={!pagination.hasNext}
        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next
      </button>
    </div>

    {/* Desktop Pagination */}
    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
      <div>
        <p className="text-sm text-gray-700">
          Showing{' '}
          <span className="font-medium">
            {(pagination.currentPage - 1) * pagination.limit + 1}
          </span>{' '}
          to{' '}
          <span className="font-medium">
            {Math.min(pagination.currentPage * pagination.limit, pagination.totalProperties)}
          </span>{' '}
          of{' '}
          <span className="font-medium">{pagination.totalProperties}</span>{' '}
          results
        </p>
      </div>
      <div>
        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          {/* Previous Button */}
          <button
            onClick={() => handleFilterChange('page', pagination.currentPage - 1)}
            disabled={!pagination.hasPrev}
            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="sr-only">Previous</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Page Numbers */}
          {(() => {
            const pages = [];
            const startPage = Math.max(1, pagination.currentPage - 2);
            const endPage = Math.min(pagination.totalPages, pagination.currentPage + 2);

            // First page
            if (startPage > 1) {
              pages.push(
                <button
                  key={1}
                  onClick={() => handleFilterChange('page', 1)}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  1
                </button>
              );
              if (startPage > 2) {
                pages.push(
                  <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    ...
                  </span>
                );
              }
            }

            // Middle pages
            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <button
                  key={i}
                  onClick={() => handleFilterChange('page', i)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                    i === pagination.currentPage
                      ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {i}
                </button>
              );
            }

            // Last page
            if (endPage < pagination.totalPages) {
              if (endPage < pagination.totalPages - 1) {
                pages.push(
                  <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    ...
                  </span>
                );
              }
              pages.push(
                <button
                  key={pagination.totalPages}
                  onClick={() => handleFilterChange('page', pagination.totalPages)}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {pagination.totalPages}
                </button>
              );
            }

            return pages;
          })()}

          {/* Next Button */}
          <button
            onClick={() => handleFilterChange('page', pagination.currentPage + 1)}
            disabled={!pagination.hasNext}
            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="sr-only">Next</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </nav>
      </div>
    </div>
  </div>
)}

</div>



      </div>
    </div>
  );
}

export default AdminProperties;
